/**
 * ActiveProjectsTable
 * canonical: lines 631–665
 *
 * .panel with 6-column .tbl:
 *   Project (name + address sub-line) | Owner | Contract | Billed | Outstanding | Status chip
 *
 * Status chips: c-grn "Active" · c-amb "Draft" · c-gry "Completed" · c-red "Lien risk"
 * Lien risk determined by cross-referencing lienAlerts prop.
 */

import { useQuery }       from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { useNavigate }    from 'react-router-dom'
import { fetchProjects, fetchLienAlerts } from '../../api/dashboard'
import { formatCurrency } from '../../lib/formatters'
import { formatOutstandingAmount } from '../../lib/projectMoneyDisplay'
import { billingProgressForProject } from '../../lib/projectMoneyDisplay'
import ContractBreakdown from '../../components/billing/ContractBreakdown'
import type { Project }   from '../../api/types'

// ── Status chip ────────────────────────────────────────────────────────────

interface ChipProps { cls: string; label: string }
function Chip({ cls, label }: ChipProps) {
  return <span className={`chip ${cls}`}>{label}</span>
}

function statusChip(project: Project, lienProjectIds: Set<number>, onLienClick?: () => void) {
  /* canonical: lines 645, 653, 661 — chip colours */
  if (lienProjectIds.has(project.id)) {
    return (
      <span
        className="chip c-red"
        onClick={e => { e.stopPropagation(); onLienClick?.() }}
        style={{ cursor: onLienClick ? 'pointer' : undefined }}
      >
        Lien risk
      </span>
    )
  }
  const map: Record<string, ChipProps> = {
    active:    { cls: 'c-grn', label: 'Active'    },
    draft:     { cls: 'c-amb', label: 'Draft'     },
    completed: { cls: 'c-gry', label: 'Completed' },
  }
  const s = map[project.status ?? 'active'] ?? { cls: 'c-gry', label: project.status ?? '—' }
  return <Chip {...s} />
}

// ── Skeleton row ───────────────────────────────────────────────────────────

function SkeletonRow() {
  const cell = { background: 'var(--line)', borderRadius: 3, height: 12, opacity: 0.5 }
  return (
    <tr>
      <td><div style={{ ...cell, width: 120 }} /></td>
      <td><div style={{ ...cell, width: 80  }} /></td>
      <td><div style={{ ...cell, width: 70  }} /></td>
      <td><div style={{ ...cell, width: 70  }} /></td>
      <td><div style={{ ...cell, width: 70  }} /></td>
      <td><div style={{ ...cell, width: 50  }} /></td>
    </tr>
  )
}

// ── ActiveProjectsTable ────────────────────────────────────────────────────

export function ActiveProjectsTable() {
  const navigate = useNavigate()
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const { data: projects, isPending: projLoading } = useQuery({
    queryKey: ['projects'],
    queryFn:  fetchProjects,
  })

  const { data: lienData } = useQuery({
    queryKey: ['lien-alerts'],
    queryFn:  fetchLienAlerts,
  })

  /* Set of project IDs that have open (unfiled) lien alerts */
  const lienProjectIds = new Set<number>(
    (lienData?.alerts ?? [])
      .filter(a => a.filed_at === null && a.days_remaining <= 30)
      .map(a => a.project_id)
  )

  const all     = projects ?? []
  const active  = all.filter(p => p.status === 'active')
  const display = all.slice(0, 8)   /* canonical shows 3; cap at 8 for real data */

  return (
    /* canonical: line 631 — .panel */
    <div className="panel">

      {/* canonical: lines 632–635 — .panel-h */}
      <div className="panel-h">
        <div className="panel-tt">
          Active projects
          {/* canonical: line 633 — .panel-st with count */}
          <span className="panel-st">
            {projLoading ? '' : `${active.length} project${active.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        <button
          className="btn b-gh btn-sm"
          onClick={() => navigate('/projects')}
        >
          View all
        </button>
      </div>

      {/* canonical: lines 636–664 — .tbl */}
      {projLoading ? (
        <table className="tbl">
          <thead>
            <tr>
              <th>Project</th><th>Owner</th>
              <th>Contract</th><th className="r">Billed</th>
              <th className="r">Outstanding</th><th></th>
            </tr>
          </thead>
          <tbody>
            {[1,2,3].map(i => <SkeletonRow key={i} />)}
          </tbody>
        </table>
      ) : all.length === 0 ? (
        /* Empty state */
        <div className="empty">
          <div className="empty-ic">⬛</div>
          <div className="empty-tt">No projects yet</div>
          <div style={{ fontSize: 12, marginBottom: 16 }}>
            Create your first project to get started
          </div>
          <button className="btn b-dk" onClick={() => navigate('/projects/new')}>
            Create project
          </button>
        </div>
      ) : (
        <table className="tbl">
          {/* canonical: line 637 — thead */}
          <thead>
            <tr>
              <th>Project</th>
              <th>Owner</th>
              {/* canonical: line 637 — Contract / Billed / Outstanding */}
              <th>Contract</th>
              <th className="r">Billed</th>
              <th className="r">Outstanding</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {display.map(p => {
              const coTotal = parseFloat(p.change_order_total ?? '0')
              const progress = billingProgressForProject(p)
              const expanded = expandedId === p.id
              const openProject = () => navigate(`/projects?pm=${p.id}`)
              return (
              <Fragment key={p.id}>
              <tr
                style={{ cursor: 'pointer' }}
                onClick={openProject}
              >
                {/* canonical: line 640 — project name + address sub-line */}
                <td>
                  <div style={{ fontWeight: 600, color: 'var(--ink)' }}>
                    {p.name ?? '—'}
                  </div>
                  {p.number && (
                    <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 2 }}>
                      #{p.number}
                    </div>
                  )}
                </td>
                {/* canonical: line 641 — owner */}
                <td onClick={e => e.stopPropagation()}>
                  {p.owner ? (
                    <button
                      type="button"
                      className="owner-statement-link"
                      data-testid={`dash-owner-name-${p.id}`}
                      onClick={() => navigate(`/statements/owner?owner=${encodeURIComponent(p.owner!)}`)}
                    >
                      {p.owner}
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                {/* canonical: line 642 — contract value (mono) */}
                <td
                  className="mono-c"
                  onClick={e => {
                    if (coTotal > 0) {
                      e.stopPropagation()
                      setExpandedId(expanded ? null : p.id)
                    }
                  }}
                  title={coTotal > 0 ? 'Click to expand contract breakdown' : undefined}
                  data-testid={`dash-contract-${p.id}`}
                >
                  {coTotal > 0 ? (
                    <span style={{ textDecoration: 'underline dotted' }}>
                      {formatCurrency(Number(p.original_contract ?? 0) + coTotal)}
                    </span>
                  ) : (
                    formatCurrency(Number(p.original_contract ?? 0))
                  )}
                </td>
                {/* canonical: line 643 — billed (right-align mono) */}
                <td className="r mono-c">
                  {formatCurrency(Number(p.total_billed ?? 0))}
                  {progress.contractTotal > 0 && coTotal > 0 && (
                    <div style={{ fontSize: 9, color: 'var(--ink4)' }}>{progress.pctBilled}% rev.</div>
                  )}
                </td>
                {/* canonical: line 644 — outstanding (right-align mono) */}
                <td className="r mono-c">
                  {formatOutstandingAmount(p.outstanding, p.total_billed)}
                </td>
                {/* canonical: line 645 — status chip */}
                <td>{statusChip(p, lienProjectIds, () => navigate(`/projects?pm=${p.id}&tab=lien`))}</td>
              </tr>
              {expanded && coTotal > 0 && (
                <tr onClick={e => e.stopPropagation()}>
                  <td colSpan={6} style={{ padding: '8px 14px', background: 'var(--bg2)', fontSize: 11 }}>
                    <ContractBreakdown
                      originalContract={p.original_contract}
                      changeOrderTotal={coTotal}
                      data-testid={`dash-contract-detail-${p.id}`}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
              )
            })}
          </tbody>
        </table>
      )}

    </div>
  )
}
